import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { ForgeIconButtonModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { LgdFormControlFactory, LgdSelectComponent } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartEvent, LegendElement, LegendItem } from "chart.js";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const enum NotificationType
{
    LeakAlerts = "Leak Alerts",
    LeakAlertsNotSent = "Leak Alerts Not Sent",
    BillingThresholdAlerts = "Billing Threshold Alerts",
    ConsumptionThresholdAlerts = "Consumption Threshold Alerts",
    MonthlyConsumptionThresholdAlerts = "Monthly Consumption Threshold Alerts",
    VacationAlerts = "Vacation Alerts"
}

@Component({
    selector: "utilities-notification-history-chart",
    templateUrl: "./notificationHistoryChart.tmpl.html",
    styleUrls: [
        "./notificationHistoryChart.component.scss"
    ],
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        LayoutDirective,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        LgdSelectComponent,
        ReactiveFormsModule,
    ]
})
export class NotificationHistoryChartComponent implements OnInit, OnDestroy
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartService = inject(ChartService);

    private readonly log = getLoggerFor("NotificationHistoryChartComponent");
    private readonly destructor$ = new Subject<void>();
    readonly formGroup: FormGroup;

    private readonly notificationTypesSubject = new BehaviorSubject<Array<NotificationType>>([
        NotificationType.LeakAlerts,
        NotificationType.LeakAlertsNotSent,
        NotificationType.BillingThresholdAlerts,
        NotificationType.ConsumptionThresholdAlerts,
        NotificationType.MonthlyConsumptionThresholdAlerts,
        NotificationType.VacationAlerts,
    ]);


    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private readonly _chartId: string;

    private _chartCriteria: IChartCriteria;

    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.formGroup = new FormGroup({
            notificationType: LgdFormControlFactory.select(undefined, false, {}),
        });
    }

    get chartId(): string | undefined { return this._chartId || this.chartCriteria?.ChartId; }

    @Input()
    get chartCriteria(): IChartCriteria | undefined { return this._chartCriteria; }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get notificationTypeControl(): AbstractControl<NotificationType> { return this.formGroup.get("notificationType"); }

    get notificationTypesObservable(): Observable<Array<NotificationType>> { return this.notificationTypesSubject.asObservable(); }

    get chartHasData(): boolean { return this.chartCriteria?.ChartDatas?.length > 0; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        (this.chartHasData
            ? Promise.resolve()
            : this.loadChartData())
            .then(
                () =>
                {
                    this.notificationTypeControl.setValue(NotificationType.LeakAlerts, { emitEvent: false });
                    this.updateChartConfiguration();
                },
                e =>
                {
                    this.log.error("Error on load", e);
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.notificationTypeControl, (_newValue: NotificationType) =>
        {
            this.addNotificationTypeParameterToCriteria();

            this.loadChartData();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.chartHasData)
                    {
                        return;
                    }
                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.log.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private addNotificationTypeParameterToCriteria(): void
    {
        let notificationTypeValue: ProfileSettingViewModel = this.chartCriteria?.Parameters?.find(x => x.Id === "NotificationType");
        if (!notificationTypeValue)
        {
            notificationTypeValue = {
                Id: "NotificationType"
            };
            this.chartCriteria?.Parameters?.push(notificationTypeValue);
        }

        notificationTypeValue.Value = this.notificationTypeControl.value;
    }

    private updateChartConfiguration(): void
    {
        switch (this.notificationTypeControl.value)
        {
            case NotificationType.BillingThresholdAlerts:
                this.chartConfig = this.displayChartForBillingThresholdAlerts();
                break;
            case NotificationType.ConsumptionThresholdAlerts:
            case NotificationType.LeakAlerts:
            case NotificationType.LeakAlertsNotSent:
            case NotificationType.MonthlyConsumptionThresholdAlerts:
            case NotificationType.VacationAlerts:
                this.chartConfig = this.displayStackedChart();
                break;
        }

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    private displayChartForBillingThresholdAlerts(): ChartConfiguration<"bar">
    {
        const chartColors = this.$chartService.getColors(2);
        const blueColor = chartColors[1];

        return {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;

                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return value;
                            },
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 2
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            backgroundColor: blueColor,
                            borderColor: blueColor,
                            hoverBackgroundColor: blueColor,
                            hoverBorderColor: blueColor,
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        }
                    ]
                    : [],
            },
        };
    }

    private displayStackedChart(): ChartConfiguration<"bar">
    {
        const chartColors = this.$chartService.getColors(6);
        const blueColor = chartColors[1];
        const orangeColor = chartColors[2];
        const redColor = chartColors[5];

        return {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                },
                elements: { line: {} },
                scales: {
                    x: {
                        stacked: true,
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        stacked: true,
                        type: "linear",
                        display: true,
                        position: "left",
                        // id: "y-axis-1",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return value;
                            },
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: blueColor,
                            borderColor: blueColor,
                            hoverBackgroundColor: blueColor,
                            hoverBorderColor: blueColor,
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        },
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            backgroundColor: orangeColor,
                            borderColor: orangeColor,
                            hoverBackgroundColor: orangeColor,
                            hoverBorderColor: orangeColor,
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        },
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[2].Data,
                            backgroundColor: redColor,
                            borderColor: redColor,
                            hoverBackgroundColor: redColor,
                            hoverBorderColor: redColor,
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        }
                    ]
                    : [],
            },
        };
    }

    displayChartParameterDialogAndReload(): void
    {
        const parameters = Utilities.deepCopy(this.chartCriteria.Parameters);
        this.$chartDialogService.show(parameters, "Notification History")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.log.debug(`Chart Parameters didn't change`);
                    return;
                }

                this.chartCriteria.Parameters = result;
                return this.loadChartData();
            });
    }
}
